<template>
    <component
        :is="getComponent"
        :to="linkToProductPage && { name: 'product', params: { id: product.id, slug: normalizeText(product.name) } }"
        :class="[(sectionStyle !== '' ? `MobileProductItem--${sectionStyle}` : ''), (isNonSaleable ? 'MobileProductItem--nonSaleable' : '')]"
        class="MobileProductItem"
    >
        <div
            v-if="getImage"
            class="MobileProductItem-photoWrapper"
        >
            <img
                :alt="getImageAlt"
                :src="getImage"
                class="MobileProductItem-photo"
            >
        </div>
        <div class="MobileProductItem-infos">
            <div class="MobileProductItem-header">
                <component
                    :is="getTitleTag"
                    class="MobileProductItem-name mb-0 text-break"
                >
                    {{ product.name.ucFirst() }}
                    <v-tooltip
                        v-if="product.isCardReaderMandatory()"
                        location="bottom"
                    >
                        <template #activator="{ props }">
                            <icomoon-icon
                                v-bind="props"
                                :alt="$t('add_to_cart_flow:card_reader_mandatory')"
                                :ariaHidden="false"
                                :layersCount="3"
                                name="CardReader"
                                class="MobileProductItem-cardReaderMandatoryIcon"
                            />
                        </template>
                        <span>{{ $t('add_to_cart_flow:card_reader_mandatory') }}</span>
                    </v-tooltip>
                </component>
                <p class="mt-0">
                    {{ product.providerNetworkLabel.ucFirst() }}
                </p>
                <v-card
                    v-if="product.shortDescription !== ''"
                    class="mb-3"
                    color="lightGray1"
                    dense
                    variant="flat"
                >
                    <v-card-text class="pa-2">
                        <p
                            class="MobileProductItem-shortDescription ma-0"
                            v-html="$sanitize(product.shortDescription)"
                        />
                    </v-card-text>
                </v-card>
                <small v-if="debugInformationsDisplay">{{ product.id }}</small>
            </div>
            <div class="MobileProductItem-footer">
                <price
                    :product="product"
                    class="MobileProductItem-price MobileProductItem-fareWrapper"
                    detail-level="REDUCED"
                    size="Small"
                />
            </div>
        </div>
        <v-btn
            v-if="showExpressPurchase"
            :title="$t('button:quick_purchase')"
            class="MobileProductItem-button rounded"
            color="secondary"
            size="small"
            variant="flat"
            icon="mdi-clock-fast"
            @click.prevent="expressPurchaseAddToCart()"
        />
        <v-btn
            v-else-if="showAddToCart"
            :title="$t('button:add')"
            class="MobileProductItem-button rounded"
            color="primary"
            size="small"
            variant="flat"
            icon="mdi-close"
            @click.prevent="addCartAction"
        >
            <template #default>
                <icomoon-icon
                    :name="'Bag--filled'"
                    class="MobileProductItem-buttonIcon"
                />
            </template>
        </v-btn>
        <v-btn
            v-if="linkToProductPage && showMoreInfo"
            color="primary"
            variant="flat"
            class="MobileProductItem-button"
            @click.prevent="addCartAction"
        >
            {{ $t('more:info') }}
        </v-btn>
        <p
            v-if="debugInformationsDisplay"
            :class="['MobileProductItem-type--' + product.modalType ]"
            class="MobileProductItem-type"
            @click.stop.prevent="showDebugModal"
        >
            {{ product.modalType }}
        </p>
    </component>
</template>

<script setup>
import IcomoonIcon from '@/StoreWeb/components/common/IcomoonIcon'
import Price from '@/StoreWeb/components/common/Price'
import { computed } from 'vue'
import { useStore } from 'vuex'
import { useProduct } from '@/StoreWeb/js/composables/useProduct'
import { normalizeText } from '@/StoreWeb/js/mixins/autocomplete-utils'

const store = useStore()

const componentProps = defineProps({
    index: {
        type: Number,
        required: true
    },
    isDisplayed: {
        type: Boolean,
        default: true
    },
    sectionStyle: {
        type: String,
        default: ''
    },
    level: {
        type: Number,
        default: 2
    },
    product: {
        type: Object,
        required: true
    },
    linkToProductPage: {
        type: Boolean,
        default: true
    }
})

const {
    getComponent,
    getImage,
    getImageAlt,
    getTitleTag,
    isNonSaleable,
    showAddToCart,
    showExpressPurchase,
    showMoreInfo,
    addCartAction,
    expressPurchaseAddToCart,
    showDebugModal
} = useProduct(componentProps)

const debugInformationsDisplay = computed(() => store.state.debuggingModule.debugInformationsDisplay)
</script>

<style lang="scss" scoped>
@import 'globalScss';

.MobileProductItem {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: $s3;
    position: relative;
    padding: $s2 0;
    border-bottom: 1px solid $color-lightgray3;
    background: #fff;
    color: $color-defaultText;

    &-photo {
        max-width: 48px;
        max-height: 48px;
    }

    &-photoWrapper {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 48px;
    }

    &-infos {
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        flex-grow: 1;
    }

    &-name {
        display: flex;
        align-items: baseline;
        margin: 0 0 $padding-small 0;
        font-family: $fontFamily-title;
        font-size: 16px;
        font-weight: $fontWeight-titleBold;

        @media (min-width: $tablet-breakpoint) {
            font-size: 20px;
        }
    }

    &-cardReaderMandatoryIcon {
        display: block;
        margin-top: 2px;
        margin-left: $s2;
        font-size: $font-medium;
        color: $color-brandSecondary;
    }

    &-targetIcon {
        font-size: 32px;
        color: $color-brandSecondary;
    }

    &-shortDescription {
        font-size: 14px;
    }

    &-price {
        margin: 0;
        padding-right: 10px;
    }

    &-button {
        &Icon {
            font-size: 20px;
        }

        &MoreInfo {
            width: auto;
            height: auto;
            padding: 8px 10px;
            border-radius: $border-radius-xsmall;
            background: $color-brandPrimary;
            font-size: 13px;
            font-weight: 500;
            color: $color-white;
        }
    }

    &-type {
        position: absolute;
        top: -8px;
        left: 10px;
        height: 16px;
        margin: 0;
        padding: 0 5px;
        background: forestgreen;
        font-size: 12px;
        line-height: 16px;
        color: #fff;

        &--MODALPACKAGE {
            background: #415971;
        }

        &--MODALPRODUCTGROUP {
            background: #ae2a2e;
        }
    }
}
</style>
